// NodeTypeWriteService.js

import { collection, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import { generateKey } from 'utilities/Keys';
import {
  formatDatabaseTypeLabel,
  getDatabaseTypeLabelFormatError,
} from 'utilities/databaseTypeLabels';
import { pluralizeTitle } from 'utilities/Languages';
import { ViewType } from 'utilities/NodeType';
import {
  getBaseCollectionByKey,
  createCollection,
} from 'services/collections';
import { createDefaultDetailsForm } from 'services/forms/FormWriteService';
import { mutateNodeType } from 'services/nodetypes/NodeTypeMutationService';
import { canBaseRole, getBaseAccessForUser } from 'services/bases/BaseRoleService';

const normalizeLabel = (label) => String(label || '').trim();
const normalizeLabelLower = (label) => normalizeLabel(label).toLowerCase();
const ALLOWED_DEFAULT_VIEW_TYPES = new Set(Object.values(ViewType));
const DEFAULT_FALLBACK_VIEW_TYPE = ViewType.LIST;
const buildDefaultDatabaseKey = (baseKey, typeKey) => (
  `${baseKey}_${typeKey}_all`.replace(/\//g, '_')
);

const normalizeDefaultView = (value, fallback = DEFAULT_FALLBACK_VIEW_TYPE) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (ALLOWED_DEFAULT_VIEW_TYPES.has(normalized)) return normalized;
  return ALLOWED_DEFAULT_VIEW_TYPES.has(fallback) ? fallback : DEFAULT_FALLBACK_VIEW_TYPE;
};

async function initializeViewsForNewType({
  userKey,
  baseKey,
  typeKey,
  labelPlural,
}) {
  if (!userKey || !baseKey || !typeKey) return;

  const databaseKey = buildDefaultDatabaseKey(baseKey, typeKey);
  const databaseTitle = normalizeLabel(labelPlural) || 'Items';

  const existingDatabase = await getBaseCollectionByKey(baseKey, databaseKey);

  if (!existingDatabase) {
    await createCollection({
      key: databaseKey,
      baseKey,
      userKey,
      database: {
        key: databaseKey,
        type: typeKey,
        title: databaseTitle,
        filters: [],
      },
    });
  }
}

export async function ensureTypeDatabaseScaffold({
  userKey,
  baseKey,
  typeKey,
  labelPlural,
} = {}) {
  await initializeViewsForNewType({
    userKey,
    baseKey,
    typeKey,
    labelPlural,
  });
}

function getBaseTypesDatabase(baseKey) {
  if (!baseKey) return null;
  return collection(db, collections.bases, baseKey, collections.nodetypes);
}

function getRootTypesDatabase() {
  return collection(db, collections.nodetypes);
}

function getFirebaseErrorDetails(error) {
  return {
    code: error?.code || null,
    message: error?.message || 'Unknown error',
  };
}

export async function baseNodeTypeExists({ baseKey, labelLower }) {
  const normalized = normalizeLabelLower(labelLower);
  const typesDatabase = getBaseTypesDatabase(baseKey);
  if (!typesDatabase || !normalized) return false;

  const q = query(typesDatabase, where('labelLower', '==', normalized), limit(1));
  const snap = await getDocs(q);
  return !snap.empty;
}

async function nodeTypeNameConflict({ baseKey, label, labelPlural }) {
  const collectionsToCheck = [
    getBaseTypesDatabase(baseKey),
    getRootTypesDatabase(),
  ].filter(Boolean);

  if (!collectionsToCheck.length) return null;

  const normalizedLabel = normalizeLabelLower(label);
  const normalizedPlural = normalizeLabelLower(labelPlural);
  if (!normalizedLabel && !normalizedPlural) return null;

  const namesToCheck = new Set([normalizedLabel, normalizedPlural].filter(Boolean));
  if (!namesToCheck.size) return null;

  const names = Array.from(namesToCheck);

  const hasConflictInDatabase = async (typesDatabase) => {
    const checks = [];

    names.forEach((name) => {
      checks.push(getDocs(query(typesDatabase, where('labelLower', '==', name), limit(1))));
      checks.push(getDocs(query(typesDatabase, where('labelPluralLower', '==', name), limit(1))));
    });

    const snapshots = await Promise.all(checks);
    return snapshots.some((snap) => !snap.empty);
  };

  const conflictChecks = await Promise.all(
    collectionsToCheck.map((typesDatabase) => hasConflictInDatabase(typesDatabase))
  );

  const hasConflict = conflictChecks.some(Boolean);

  if (hasConflict) {
    return 'A type with that label already exists in your available type set.';
  }

  return null;
}

export async function createBaseNodeType({
  label,
  labelPlural,
  description,
  iconKey,
  defaultView,
  userKey,
  baseKey,
  anchorNodeKey,
} = {}) {
  const cleanedLabel = formatDatabaseTypeLabel(label);
  if (!cleanedLabel) {
    throw new Error('Please enter a label.');
  }

  const labelFormatError = getDatabaseTypeLabelFormatError(cleanedLabel);
  if (labelFormatError) {
    throw new Error(labelFormatError);
  }

  if (!baseKey) {
    throw new Error('No space selected.');
  }

  if (!userKey) {
    throw new Error('You must be logged in.');
  }

  const actorAccess = await getBaseAccessForUser({
    baseKey,
    userKey,
  });

  if (!canBaseRole(actorAccess?.roleKey, 'types.create', {
    tier: actorAccess?.tier,
    isCustom: actorAccess?.isCustom,
    roleDefinition: actorAccess?.roleDefinition,
    roleMissing: actorAccess?.roleMissing,
  })) {
    throw new Error('You do not have permission to create types in this base.');
  }

  const labelLower = normalizeLabelLower(cleanedLabel);
  const cleanedPlural = formatDatabaseTypeLabel(labelPlural) || pluralizeTitle(cleanedLabel);
  const pluralFormatError = getDatabaseTypeLabelFormatError(cleanedPlural);
  if (pluralFormatError) {
    throw new Error(pluralFormatError);
  }
  const labelPluralLower = normalizeLabelLower(cleanedPlural);
  const cleanedDescription = normalizeLabel(description);
  const cleanedIconKey = normalizeLabel(iconKey);
  const cleanedDefaultView = normalizeDefaultView(defaultView, DEFAULT_FALLBACK_VIEW_TYPE);
  const conflictMessage = await nodeTypeNameConflict({
    baseKey,
    label: cleanedLabel,
    labelPlural: cleanedPlural,
  });
  if (conflictMessage) {
    throw new Error(conflictMessage);
  }

  let key = generateKey();
  const now = Timestamp.now();

  const payload = {
    key,
    userKey,
    baseKey,
    label: cleanedLabel,
    labelLower,
    labelPlural: cleanedPlural,
    labelPluralLower,
    description: cleanedDescription,
    allowedParents: [],
    autosizeHighlight: true,
    defaultView: cleanedDefaultView,
    iconKey: cleanedIconKey || 'deployed_code',
    photoUrl: null,
    prioritySort: [],
    schema: { fields: [] },
    showHighlight: false,
    suggestedChildren: [],
    showPeekOnOpen: true,
    allowTopLevelCreate: true,
    showInMenu: false,
    showInAddMenu: true,
    version: 1,
    dateCreated: now,
    dateModified: now,
  };

  try {
    const result = await mutateNodeType({
      action: 'type.create',
      baseKey,
      label: cleanedLabel,
      labelPlural: cleanedPlural,
      description: cleanedDescription,
      iconKey: cleanedIconKey || 'deployed_code',
      defaultView: cleanedDefaultView,
    });

    if (result?.key) {
      key = result.key;
      payload.key = result.key;
    }
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage typeDocWrite (${details.code || 'no-code'}): ${details.message}`);
  }

  try {
    await initializeViewsForNewType({
      userKey,
      baseKey,
      typeKey: key,
      labelPlural: cleanedPlural,
      defaultView: cleanedDefaultView,
      anchorNodeKey,
    });
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage initializeViewsForNewType (${details.code || 'no-code'}): ${details.message}`);
  }

  try {
    await createDefaultDetailsForm({
      typeDef: payload,
      typeKey: key,
      userKey,
      baseKey,
    });
  } catch (error) {
    const details = getFirebaseErrorDetails(error);
    throw new Error(`Type create denied at stage createDefaultDetailsForm (${details.code || 'no-code'}): ${details.message}`);
  }

  return payload;
}
